import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Mail, Send, Car, FileText, UserPlus, Loader2, X,
  Phone, Link2, MessageCircle, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import api from '@/api/api';
import { toast } from 'sonner';

const DDI_OPTIONS = [
  { code: '+351', label: 'PT +351' },
  { code: '+55', label: 'BR +55' },
  { code: '+34', label: 'ES +34' },
  { code: '+33', label: 'FR +33' },
  { code: '+44', label: 'UK +44' }
];

export default function DriverInviteManager({ isOpen, onClose, onInviteSent }) {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [invites, setInvites] = useState([]);
  const [fleetRefCode, setFleetRefCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [linking, setLinking] = useState(false);
  const [copied, setCopied] = useState(false);
  const [driverCode, setDriverCode] = useState('');

  const [inviteData, setInviteData] = useState({
    email: '',
    phone: '',
    country_code: '+351',
    template_id: '',
    vehicle_id: ''
  });

  const effectiveOpen = typeof isOpen === 'boolean' ? isOpen : true;
  const isPageMode = typeof isOpen !== 'boolean';
  const handleClose = typeof onClose === 'function' ? onClose : () => navigate('/compliance');

  const fetchInvites = async () => {
    try {
      const response = await api.get('/fleets/invites');
      setInvites(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (_) {
      toast.error('Não foi possível carregar a listagem de convites.');
    }
  };

  useEffect(() => {
    if (!effectiveOpen) return;
    const loadInitialData = async () => {
      try {
        setLoading(true);
        const [resTpl, resVeh, resFleet, resInvites] = await Promise.all([
          api.get('/contracts/templates'),
          api.get('/vehicles?status=active'),
          api.get('/fleets/my-fleet'),
          api.get('/fleets/invites')
        ]);
        setTemplates(resTpl.data?.data || []);
        setVehicles(resVeh.data?.data || []);
        setFleetRefCode(resFleet.data?.data?.reference_code || '');
        setInvites(Array.isArray(resInvites.data?.data) ? resInvites.data.data : []);
      } catch (_) {
        toast.error('Falha ao carregar dados de convites.');
      } finally {
        setLoading(false);
      }
    };
    loadInitialData();
  }, [effectiveOpen]);

  const handleSendInvite = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const payload = {
        driverEmail: inviteData.email,
        driverPhone: inviteData.phone,
        driverCountryCode: inviteData.country_code,
        vehicleId: inviteData.vehicle_id || null,
        templateId: inviteData.template_id || null
      };
      const response = await api.post('/fleets/invite-driver', payload);
      toast.success(response.data?.message || 'Convite criado com sucesso.');
      setInviteData({
        email: '',
        phone: '',
        country_code: '+351',
        template_id: '',
        vehicle_id: ''
      });
      await fetchInvites();
      if (onInviteSent) onInviteSent();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Falha ao enviar convite.');
    } finally {
      setSending(false);
    }
  };

  const handleLinkByDriverCode = async () => {
    if (!driverCode.trim()) return;
    setLinking(true);
    try {
      const response = await api.post('/fleets/link-driver-by-code', { driverCode });
      toast.success(response.data?.message || 'Pedido de vínculo enviado para aprovação do motorista.');
      setDriverCode('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Não foi possível vincular este motorista.');
    } finally {
      setLinking(false);
    }
  };

  const copyFleetCode = async () => {
    if (!fleetRefCode) return;
    await navigator.clipboard.writeText(fleetRefCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 1300);
  };

  const openWhatsApp = (invite) => {
    const countryCode = (invite.driver_country_code || '+351').replace('+', '');
    const phone = String(invite.driver_phone || '').replace(/\D/g, '');
    if (!phone) {
      toast.error('Convite sem telefone do motorista.');
      return;
    }
    const inviteUrl = `${window.location.origin}/accept-invite/${invite.token}`;
    const message = encodeURIComponent(
      `Convite de frota\nCódigo da frota: ${invite.fleet_reference_code || fleetRefCode || '-'}\nLink do convite: ${inviteUrl}\nEmail: ${invite.driver_email || '-'}`
    );
    window.open(`https://wa.me/${countryCode}${phone}?text=${message}`, '_blank', 'noopener,noreferrer');
  };

  const statusLabel = (status) => {
    if (status === 'accepted') return 'Aceite';
    if (status === 'rejected') return 'Recusado';
    if (status === 'expired') return 'Expirado';
    return 'Pendente';
  };

  const statusClasses = (status) => {
    if (status === 'accepted') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    if (status === 'rejected') return 'bg-red-100 text-red-700 border-red-200';
    if (status === 'expired') return 'bg-muted text-muted-foreground border-border';
    return 'bg-amber-100 text-amber-700 border-amber-200';
  };

  const content = (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-start">
        <div className="flex gap-4 items-center">
          <div className="p-3 bg-primary text-primary-foreground rounded-xl">
            <UserPlus size={24} />
          </div>
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Convites & Vínculo</h2>
            <p className="text-xs text-muted-foreground uppercase">Email + WhatsApp + Código de Integração</p>
          </div>
        </div>
        {isPageMode ? null : (
          <Button variant="ghost" size="icon" onClick={handleClose}>
            <X size={20} />
          </Button>
        )}
      </div>

      {loading ? (
        <div className="py-20 flex flex-col items-center justify-center gap-4">
          <Loader2 className="animate-spin text-primary" size={32} />
          <p className="text-xs text-muted-foreground uppercase">Sincronizando convites...</p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-slate-900 text-white border-0">
              <CardContent className="p-6 space-y-3">
                <p className="text-xs uppercase font-bold tracking-widest text-primary">Código de Referência da Frota</p>
                <div className="flex items-center justify-between gap-3">
                  <span className="font-mono text-xl font-bold">{fleetRefCode || 'PENDENTE'}</span>
                  <Button type="button" onClick={copyFleetCode} variant="outline" size="sm" className="text-slate-900 border-white/20">
                    {copied ? 'Copiado' : 'Copiar'}
                  </Button>
                </div>
                <p className="text-xs text-slate-300">
                  O motorista pode usar este código no cadastro para solicitar vínculo à sua frota.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6 space-y-3">
                <p className="text-xs uppercase font-bold tracking-widest text-muted-foreground">Vincular Motorista por Código</p>
                <div className="flex items-center gap-3">
                  <Input
                    type="text"
                    value={driverCode}
                    onChange={(e) => setDriverCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12))}
                    placeholder="DRV000123"
                    className="font-mono uppercase"
                  />
                  <Button
                    type="button"
                    disabled={linking || !driverCode}
                    onClick={handleLinkByDriverCode}
                  >
                    {linking ? <Loader2 size={16} className="animate-spin" /> : <Link2 size={16} />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">Para motoristas já cadastrados que enviam o próprio código de motorista.</p>
              </CardContent>
            </Card>
          </div>

          <form onSubmit={handleSendInvite} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="p-6 space-y-4">
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase text-muted-foreground">Email do Motorista</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                    <Input
                      type="email"
                      required
                      className="pl-10"
                      placeholder="motorista@vetorstack.com"
                      value={inviteData.email}
                      onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-1 space-y-2">
                    <label className="text-xs font-semibold uppercase text-muted-foreground">DDI</label>
                    <select
                      className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      value={inviteData.country_code}
                      onChange={(e) => setInviteData({ ...inviteData, country_code: e.target.value })}
                    >
                      {DDI_OPTIONS.map((ddi) => (
                        <option key={ddi.code} value={ddi.code}>{ddi.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-2 space-y-2">
                    <label className="text-xs font-semibold uppercase text-muted-foreground">WhatsApp</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                      <Input
                        type="text"
                        required
                        className="pl-10"
                        placeholder="9XXXXXXXX"
                        value={inviteData.phone}
                        onChange={(e) => setInviteData({ ...inviteData, phone: e.target.value.replace(/\D/g, '').slice(0, 15) })}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6 space-y-4">
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase text-muted-foreground">Viatura (opcional)</label>
                  <div className="relative">
                    <Car className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                    <select
                      className="flex h-9 w-full rounded-md border border-input bg-background pl-10 pr-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      value={inviteData.vehicle_id}
                      onChange={(e) => setInviteData({ ...inviteData, vehicle_id: e.target.value })}
                    >
                      <option value="">Sem viatura fixa</option>
                      {vehicles.map((v) => (
                        <option key={v.id} value={v.id}>{v.license_plate} - {v.brand} {v.model}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase text-muted-foreground">Template Contratual (opcional)</label>
                  <div className="relative">
                    <FileText className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                    <select
                      className="flex h-9 w-full rounded-md border border-input bg-background pl-10 pr-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      value={inviteData.template_id}
                      onChange={(e) => setInviteData({ ...inviteData, template_id: e.target.value })}
                    >
                      <option value="">Template padrão</option>
                      {templates.map((tpl) => (
                        <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <Button type="submit" disabled={sending} className="w-full">
                  {sending ? <Loader2 className="animate-spin" size={20} /> : <Send size={18} />}
                  <span className="ml-2">Enviar Convite</span>
                </Button>
              </CardContent>
            </Card>
          </form>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold uppercase text-muted-foreground">Convites Enviados</CardTitle>
              <Button variant="outline" size="icon" onClick={fetchInvites}>
                <RefreshCw size={14} />
              </Button>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground uppercase">Motorista</th>
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground uppercase">WhatsApp</th>
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground uppercase">Status</th>
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground uppercase">Expira</th>
                      <th className="px-4 py-3 text-right font-medium text-muted-foreground uppercase">Ação</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {invites.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-xs text-muted-foreground uppercase">
                          Nenhum convite registado.
                        </td>
                      </tr>
                    ) : (
                      invites.map((invite) => (
                        <tr key={invite.id} className="hover:bg-muted/50">
                          <td className="px-4 py-3">
                            <div className="flex flex-col">
                              <span className="font-semibold">{invite.driver_email || '-'}</span>
                              <span className="text-xs text-muted-foreground uppercase">REF: {invite.token?.slice(0, 10)}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {invite.driver_country_code || '+351'} {invite.driver_phone || '-'}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase border ${statusClasses(invite.status)}`}>
                              {statusLabel(invite.status)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {invite.expires_at ? new Date(invite.expires_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => openWhatsApp(invite)}
                            >
                              <MessageCircle size={14} className="mr-2" />
                              WhatsApp
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );

  return isPageMode ? (
    <div className="max-w-6xl mx-auto w-full space-y-6">
      {content}
    </div>
  ) : content;
}
